import { ChainContractsRegister } from "@gearbox-protocol/sdk";
import {
  AddressProviderContract,
  BYTECODE_REPOSITORY,
  BytecodeRepositoryContract,
  CROSS_CHAIN_GOVERNANCE,
  CrossChainMultisigContract,
  INSTANCE_MANAGER,
  InstanceManagerContract,
  MARKET_CONFIGURATOR_FACTORY,
  MarketConfiguratorFactoryContract,
  PRICE_FEED_STORE,
  PriceFeedStoreContract,
} from "@gearbox-protocol/sdk/permissionless";
import { useEffect, useState } from "react";
import { Address, zeroAddress } from "viem";
import { ClientConfig } from "@/config/client";

export function useWeb3() {
  const [contracts, setContracts] = useState<{
    addressProvider?: AddressProviderContract;
    bytecodeRepository?: BytecodeRepositoryContract;
    instanceManager?: InstanceManagerContract;
    crossChainMultisig?: CrossChainMultisigContract;
    priceFeedStore?: PriceFeedStoreContract;
    marketConfiguratorFactory?: MarketConfiguratorFactoryContract;
  }>({});

  useEffect(() => {
    async function initContracts() {
      try {
        const config = ClientConfig.getInstance();

        // Initialize AddressProvider first
        const addressProvider = new AddressProviderContract(
          config.addressProvider,
          config.client,
        );

        // Helper function to get contract address
        const getAddress = async (key: string): Promise<Address> => {
          try {
            return await addressProvider.getAddressOrRevert(key, 0n);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch {
            return zeroAddress;
          }
        };

        // Initialize all addresses in parallel
        const [
          bytecodeRepositoryAddress,
          instanceManagerAddress,
          crossChainMultisigAddress,
          priceFeedStoreAddress,
          marketConfiguratorFactoryAddress,
        ] = await Promise.all([
          getAddress(BYTECODE_REPOSITORY),
          getAddress(INSTANCE_MANAGER),
          getAddress(CROSS_CHAIN_GOVERNANCE),
          getAddress(PRICE_FEED_STORE),
          getAddress(MARKET_CONFIGURATOR_FACTORY),
        ]);

        const register = new ChainContractsRegister(config.client);

        const bytecodeRepository = new BytecodeRepositoryContract(
          bytecodeRepositoryAddress,
          config.client,
        );

        const instanceManager = new InstanceManagerContract(
          instanceManagerAddress,
          register,
        );

        const crossChainMultisig = new CrossChainMultisigContract(
          crossChainMultisigAddress,
          register,
        );

        const priceFeedStore = new PriceFeedStoreContract(
          priceFeedStoreAddress,
          config.client,
        );

        const marketConfiguratorFactory = new MarketConfiguratorFactoryContract(
          marketConfiguratorFactoryAddress,
          config.client,
        );

        setContracts({
          addressProvider,
          bytecodeRepository,
          instanceManager,
          crossChainMultisig,
          priceFeedStore,
          marketConfiguratorFactory,
        });
      } catch (error) {
        console.error("Failed to initialize contracts:", error);
      }
    }

    if (
      Object.values(contracts).length > 0 &&
      Object.values(contracts).every(contract => contract !== undefined)
    )
      return;

    initContracts();
  }, [contracts]);

  return {
    ...contracts,
  };
}
